import * as React from "react";
import { Alert } from "@mui/material";
import { useSelector } from "react-redux";
import CclGenericAccordian from "../../../../../../components/common/cclGenericAccordian";
import { useAreaPreference } from "../../../../../../hooks/useAreaPreference";
import { updateSessionUninvitedParticipantsGridState } from "../../../../../../app/slices/sessionUninvitedParticipantsTableStateSlice";
import { useGetCclParticipantsByEventKeyQuery } from "../../../../../../services/cclTokenedEnterpriseParticipantApi";
import { Participant } from "../../../../../../services/types/enterpriseParticipantApiTypes";
import { SessionDetailType } from "../../../../../../services/types/sessionDetailTypes";
import SessionParticipantDatesListDataGrid from "../../../participant/sessionParticipantDatesListDataGrid";

interface UninvitedParticipantsCardProps {
    session: SessionDetailType;
}

// a null date, or the 9999-12-31 "no date" sentinel (rendered blank in the grid), means no invitation was sent
const isUninvited = (p: Participant): boolean => {
    const invitation = p.actualPreworkInvitation as Date | string | null;
    return invitation == null || String(invitation).startsWith("9999-");
};

const UninvitedParticipantsCard: React.FC<UninvitedParticipantsCardProps> = (props) => {
    const [expanded, setExpanded] = useAreaPreference<boolean>(
        "sessionDetail",
        "assessments.uninvitedExpanded",
        true,
    );
    const tableState = useSelector((state: any) => state.sessionUninvitedParticipantsTableState);

    const {
        data: participants,
        isFetching,
        isError,
    } = useGetCclParticipantsByEventKeyQuery(props.session.sessionKey.toString());

    const uninvitedParticipants = React.useMemo(
        () => (participants ?? []).filter(isUninvited),
        [participants],
    );

    return (
        <CclGenericAccordian
            headingText="Un-Invited Participants"
            noGutter={true}
            sx={{ mt: 2 }}
            expanded={expanded}
            onExpandedChange={setExpanded}
        >
            {isError && participants == null ? (
                <Alert severity="error">
                    There was an error retrieving the participants for this session.
                </Alert>
            ) : (
                <SessionParticipantDatesListDataGrid
                    participants={uninvitedParticipants}
                    showAttendance={false}
                    session={props.session}
                    participantsRefetching={isFetching}
                    exportNameSuffix="UninvitedParticipants"
                    showOnlyConfirmedPreferenceKey="assessments.uninvitedShowOnlyConfirmed"
                    tableStateSelector={tableState}
                    tableStateActionCreator={updateSessionUninvitedParticipantsGridState}
                    hideActions
                    autoHeight
                />
            )}
        </CclGenericAccordian>
    );
};

export default UninvitedParticipantsCard;
